import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, CalendarPlus, Calculator, Pencil, Plus, RotateCcw, Save, Search, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { formatApiError } from "../lib/api";
import { getAiMealDraftStorageKey } from "../lib/aiMealBuilder";
import { localizeCategory, localizeFoodName, localizeServing } from "../lib/foodTranslations";
import { supabase } from "../lib/supabase";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const NUTRIENTS = [
  { key: "potassium_mg", labelKey: "potassium", unit: "mg", color: "var(--rs-potassium)" },
  { key: "phosphorus_mg", labelKey: "phosphorus", unit: "mg", color: "var(--rs-phosphorus)" },
  { key: "sodium_mg", labelKey: "sodium", unit: "mg", color: "var(--rs-sodium)" },
  { key: "protein_g", labelKey: "protein", unit: "g", color: "var(--rs-protein)" },
];
const MIXED_MEAL_CATEGORY = "Mixed Meal";
const DEFAULT_MEAL_NAME = "Mixed Meal";

const hasNutrientValue = (value) =>
  value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value));

const formatAmount = (value, unit) => {
  if (!Number.isFinite(value)) return "-";
  const decimals = unit === "g" ? 1 : 0;
  const rounded = Number(value.toFixed(decimals));
  return `${rounded.toLocaleString(undefined, {
    maximumFractionDigits: decimals,
    minimumFractionDigits: rounded % 1 === 0 ? 0 : decimals,
  })} ${unit}`;
};

const parsePositiveQuantity = (value) => {
  const quantity = Number(value);
  return Number.isFinite(quantity) && quantity > 0 ? quantity : null;
};

const normalizeText = (value) => String(value || "").trim().toLocaleLowerCase();

const categoryLabel = (category) => category || "Uncategorized";

const historyAmount = (value, unit) => {
  if (!Number.isFinite(value)) return 0;
  return unit === "g" ? Number(value.toFixed(1)) : Math.round(value);
};

const calculateTotals = (mealItems) => {
  return mealItems.reduce(
    (acc, item) => {
      const itemQuantity = parsePositiveQuantity(item.quantity);
      if (!itemQuantity) return acc;

      NUTRIENTS.forEach((nutrient) => {
        const rawValue = item.food?.[nutrient.key];
        if (hasNutrientValue(rawValue)) {
          acc[nutrient.key] += Number(rawValue) * itemQuantity;
        }
      });

      return acc;
    },
    { potassium_mg: 0, phosphorus_mg: 0, sodium_mg: 0, protein_g: 0 }
  );
};

const serializeFood = (food) => {
  if (!food) return null;

  return {
    id: food.id,
    name: food.name,
    name_en: food.name_en,
    name_ar: food.name_ar,
    category: food.category,
    serving_size: food.serving_size,
    potassium_mg: food.potassium_mg,
    phosphorus_mg: food.phosphorus_mg,
    sodium_mg: food.sodium_mg,
    protein_g: food.protein_g,
  };
};

const mealHasMissingData = (meal) =>
  (meal?.items || []).some((item) =>
    NUTRIENTS.some((nutrient) => !hasNutrientValue(item.food?.[nutrient.key]))
  );

const readJsonArray = (key) => {
  if (typeof window === "undefined") return [];

  try {
    const saved = localStorage.getItem(key);
    const parsed = saved ? JSON.parse(saved) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    localStorage.removeItem(key);
    return [];
  }
};

const readGuestWeight = () => {
  if (typeof window === "undefined") return null;

  try {
    const savedProfile = localStorage.getItem("guestProfile");
    const guestProfile = savedProfile ? JSON.parse(savedProfile) : null;
    const weight = Number(guestProfile?.weight_kg);
    return Number.isFinite(weight) && weight > 0 ? weight : null;
  } catch {
    localStorage.removeItem("guestProfile");
    return null;
  }
};

export default function MealMixer() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const isArabic = lang === "ar";
  const guestMode =
    typeof window !== "undefined" &&
    localStorage.getItem("guestMode") === "true";

  const [foods, setFoods] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [selectedFoodId, setSelectedFoodId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [mealName, setMealName] = useState("");
  const [items, setItems] = useState([]);
  const [validation, setValidation] = useState("");
  const [weightKg, setWeightKg] = useState(null);
  const [storageReady, setStorageReady] = useState(false);
  const [savedMeals, setSavedMeals] = useState([]);
  const [savedMealsReady, setSavedMealsReady] = useState(false);
  const [editingMealId, setEditingMealId] = useState(null);
  const [savingHistoryId, setSavingHistoryId] = useState(null);
  const [aiMealDraft, setAiMealDraft] = useState(null);

  const displayFoodName = useCallback((food) => localizeFoodName(food?.name, lang), [lang]);
  const displayCategory = useCallback((value) => localizeCategory(value, lang), [lang]);
  const displayServing = useCallback((serving) => localizeServing(serving, lang), [lang]);
  const storageKey = guestMode ? "ddp-meal-mixer-guest" : user?.id ? `ddp-meal-mixer-${user.id}` : null;
  const savedMealsKey = guestMode
    ? "ddp-saved-mixed-meals-guest"
    : user?.id
      ? `ddp-saved-mixed-meals-${user.id}`
      : null;
  const aiMealDraftKey = getAiMealDraftStorageKey({ guestMode, userId: user?.id });

  const loadFoods = useCallback(async () => {
    const { data, error } = await supabase.from("foods").select("*");
    if (error) {
      toast.error(formatApiError(error));
      return;
    }
    setFoods(data || []);
  }, []);

  const loadWeight = useCallback(async () => {
    if (guestMode) {
      setWeightKg(readGuestWeight());
      return;
    }

    if (!user?.id) {
      setWeightKg(null);
      return;
    }

    const { data, error } = await supabase
      .from("profiles")
      .select("weight_kg")
      .eq("id", user.id)
      .maybeSingle();

    if (error) {
      toast.error(formatApiError(error));
      setWeightKg(null);
      return;
    }

    const savedWeight = Number(data?.weight_kg);
    setWeightKg(Number.isFinite(savedWeight) && savedWeight > 0 ? savedWeight : null);
  }, [guestMode, user?.id]);

  useEffect(() => {
    loadFoods();
  }, [loadFoods]);

  useEffect(() => {
    loadWeight();
  }, [loadWeight]);

  useEffect(() => {
    setStorageReady(false);

    if (!storageKey || typeof window === "undefined") {
      setItems([]);
      setMealName("");
      setEditingMealId(null);
      setStorageReady(true);
      return;
    }

    try {
      const saved = localStorage.getItem(storageKey);
      const parsed = saved ? JSON.parse(saved) : null;
      setItems(Array.isArray(parsed?.items) ? parsed.items : []);
      setMealName(typeof parsed?.mealName === "string" ? parsed.mealName : "");
      setEditingMealId(typeof parsed?.editingMealId === "string" ? parsed.editingMealId : null);
    } catch {
      localStorage.removeItem(storageKey);
      setItems([]);
      setMealName("");
      setEditingMealId(null);
    } finally {
      setStorageReady(true);
    }
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey || !storageReady || typeof window === "undefined") return;

    if (!items.length && !mealName.trim() && !editingMealId) {
      localStorage.removeItem(storageKey);
      return;
    }

    localStorage.setItem(storageKey, JSON.stringify({ mealName, items, editingMealId }));
  }, [editingMealId, items, mealName, storageKey, storageReady]);

  useEffect(() => {
    setSavedMealsReady(false);

    if (!savedMealsKey || typeof window === "undefined") {
      setSavedMeals([]);
      setSavedMealsReady(true);
      return;
    }

    try {
      const saved = localStorage.getItem(savedMealsKey);
      const parsed = saved ? JSON.parse(saved) : [];
      setSavedMeals(Array.isArray(parsed) ? parsed : []);
    } catch {
      localStorage.removeItem(savedMealsKey);
      setSavedMeals([]);
    } finally {
      setSavedMealsReady(true);
    }
  }, [savedMealsKey]);

  useEffect(() => {
    if (!savedMealsKey || !savedMealsReady || typeof window === "undefined") return;

    if (!savedMeals.length) {
      localStorage.removeItem(savedMealsKey);
      return;
    }

    localStorage.setItem(savedMealsKey, JSON.stringify(savedMeals));
  }, [savedMeals, savedMealsKey, savedMealsReady]);

  useEffect(() => {
    if (!aiMealDraftKey || typeof window === "undefined") {
      setAiMealDraft(null);
      return;
    }

    try {
      const saved = localStorage.getItem(aiMealDraftKey);
      const parsed = saved ? JSON.parse(saved) : null;
      setAiMealDraft(parsed?.mealName && Array.isArray(parsed?.ingredients) ? parsed : null);
    } catch {
      localStorage.removeItem(aiMealDraftKey);
      setAiMealDraft(null);
    }
  }, [aiMealDraftKey]);

  useEffect(() => {
    if (!foods.length || !items.length) return;

    setItems((current) =>
      current.map((item) => {
        const freshFood = foods.find((food) => String(food.id) === String(item.food?.id));
        return freshFood ? { ...item, food: freshFood } : item;
      })
    );
  }, [foods, items.length]);

  const categories = useMemo(() => {
    return [...new Set(foods.map((food) => categoryLabel(food.category)))].sort((a, b) =>
      a.localeCompare(b)
    );
  }, [foods]);

  const filteredFoods = useMemo(() => {
    const query = normalizeText(search);

    return foods
      .filter((food) => {
        const names = [
          food.name,
          food.name_en,
          food.name_ar,
          localizeFoodName(food.name, "en"),
          localizeFoodName(food.name, "ar"),
        ];
        const matchesSearch = !query || names.some((name) => normalizeText(name).includes(query));
        const matchesCategory = category === "all" || categoryLabel(food.category) === category;

        return matchesSearch && matchesCategory;
      })
      .sort((a, b) =>
        normalizeText(displayFoodName(a) || a.name).localeCompare(normalizeText(displayFoodName(b) || b.name))
      );
  }, [category, displayFoodName, foods, search]);

  const selectedFood = useMemo(
    () => foods.find((food) => String(food.id) === String(selectedFoodId)),
    [foods, selectedFoodId]
  );

  const selectedHasMissingData = useMemo(() => {
    if (!selectedFood) return false;
    return NUTRIENTS.some((nutrient) => !hasNutrientValue(selectedFood[nutrient.key]));
  }, [selectedFood]);

  const totals = useMemo(() => calculateTotals(items), [items]);

  const proteinTarget = Number.isFinite(weightKg) && weightKg > 0 ? weightKg * 1.2 : null;
  const remainingProtein = proteinTarget === null ? null : proteinTarget - totals.protein_g;
  const hasInvalidMixQuantity = items.some((item) => !parsePositiveQuantity(item.quantity));
  const canSaveMix = items.length > 0 && !hasInvalidMixQuantity;

  const addItem = () => {
    if (!selectedFood) {
      setValidation(t("meal_mixer_select_food"));
      return;
    }

    const parsedQuantity = parsePositiveQuantity(quantity);
    if (!parsedQuantity) {
      setValidation(t("meal_mixer_invalid_quantity"));
      return;
    }

    const nextItem = {
      id: crypto.randomUUID(),
      food: selectedFood,
      quantity: String(parsedQuantity),
    };

    setItems((current) => [nextItem, ...current]);
    setValidation("");
    setQuantity("1");
  };

  const updateItemQuantity = (itemId, nextQuantity) => {
    setItems((current) =>
      current.map((item) => (item.id === itemId ? { ...item, quantity: nextQuantity } : item))
    );
  };

  const removeItem = (itemId) => {
    setItems((current) => current.filter((item) => item.id !== itemId));
  };

  const resetMeal = () => {
    setItems([]);
    setMealName("");
    setEditingMealId(null);
    setValidation("");
    if (storageKey && typeof window !== "undefined") {
      localStorage.removeItem(storageKey);
    }
  };

  const removeAiMealDraft = () => {
    if (aiMealDraftKey && typeof window !== "undefined") {
      localStorage.removeItem(aiMealDraftKey);
    }
    setAiMealDraft(null);
  };

  const importAiMealDraft = () => {
    if (!aiMealDraft?.ingredients?.length) return;

    const draftItems = aiMealDraft.ingredients
      .map((ingredient) => {
        const food =
          foods.find((item) => String(item.id) === String(ingredient.foodId || ingredient.foodKey)) ||
          ingredient.food;

        if (!food?.id) return null;

        return {
          id: crypto.randomUUID(),
          food,
          quantity: String(parsePositiveQuantity(ingredient.quantity) || 1),
        };
      })
      .filter(Boolean);

    if (!draftItems.length) {
      setValidation(t("meal_mixer_ai_draft_no_matches"));
      return;
    }

    setMealName(aiMealDraft.mealName || "");
    setItems(draftItems);
    setEditingMealId(null);
    setValidation(aiMealDraft.needsQuantityReview ? t("meal_mixer_ai_draft_quantity_review") : "");
    removeAiMealDraft();
    toast.success(t("meal_mixer_ai_draft_imported"));
  };

  const nutrientContribution = (food, nutrient, itemQuantity) => {
    if (!hasNutrientValue(food?.[nutrient.key])) return null;
    return Number(food[nutrient.key]) * itemQuantity;
  };

  const saveMixedMeal = ({ saveAsNew = false } = {}) => {
    const cleanMealName = mealName.trim();
    if (!cleanMealName) {
      setValidation(t("meal_mixer_name_required"));
      return;
    }

    if (!canSaveMix) {
      setValidation(t("meal_mixer_invalid_quantity"));
      return;
    }

    const now = new Date().toISOString();
    const savedItems = items.map((item) => {
      const parsedQuantity = parsePositiveQuantity(item.quantity);
      return {
        id: item.id || crypto.randomUUID(),
        food: serializeFood(item.food),
        quantity: parsedQuantity,
      };
    });
    const savedTotals = calculateTotals(savedItems);
    const shouldUpdate = editingMealId && !saveAsNew;
    const existingMeal = shouldUpdate
      ? savedMeals.find((meal) => String(meal.id) === String(editingMealId))
      : null;
    const mealId = shouldUpdate ? editingMealId : crypto.randomUUID();
    const nextMeal = {
      id: mealId,
      name: cleanMealName,
      items: savedItems,
      totals: {
        potassium_mg: savedTotals.potassium_mg,
        phosphorus_mg: savedTotals.phosphorus_mg,
        sodium_mg: savedTotals.sodium_mg,
        protein_g: savedTotals.protein_g,
      },
      createdAt: existingMeal?.createdAt || now,
      updatedAt: now,
    };

    setSavedMeals((current) => {
      const withoutMeal = current.filter((meal) => String(meal.id) !== String(mealId));
      return [nextMeal, ...withoutMeal];
    });
    setEditingMealId(mealId);
    setValidation("");
    toast.success(t(shouldUpdate ? "meal_mixer_saved_meal_updated" : "meal_mixer_saved_meal_saved"));
  };

  const loadSavedMeal = (meal) => {
    setMealName(meal.name || "");
    setItems(
      (meal.items || []).map((item) => ({
        id: item.id || crypto.randomUUID(),
        food: item.food,
        quantity: String(item.quantity || "1"),
      }))
    );
    setEditingMealId(meal.id);
    setValidation("");
  };

  const deleteSavedMeal = (mealId) => {
    setSavedMeals((current) => current.filter((meal) => String(meal.id) !== String(mealId)));
    if (String(editingMealId) === String(mealId)) {
      setEditingMealId(null);
    }
    toast.success(t("meal_mixer_saved_meal_deleted"));
  };

  const addSavedMealToHistory = async (meal) => {
    const mixedMealIngredients = (meal.items || [])
      .map((item) => ({
        name: item.food?.name || item.food?.name_en || item.food?.name_ar || "",
        quantity: Number(item.quantity || 1) || 1,
      }))
      .filter((ingredient) => ingredient.name);
    const ingredientSummary = mixedMealIngredients
      .map((ingredient) => `${ingredient.name} x ${ingredient.quantity}`)
      .join(", ");
    const itemCount = meal.items?.length || 0;
    const historyPayload = {
      food_id: null,
      food_name: meal.name || DEFAULT_MEAL_NAME,
      category: MIXED_MEAL_CATEGORY,
      serving_size: `${itemCount} ${itemCount === 1 ? "item" : "items"}${ingredientSummary ? `: ingredients: ${ingredientSummary}` : ""}`,
      quantity: 1,
      potassium_mg: historyAmount(Number(meal.totals?.potassium_mg || 0), "mg"),
      phosphorus_mg: historyAmount(Number(meal.totals?.phosphorus_mg || 0), "mg"),
      sodium_mg: historyAmount(Number(meal.totals?.sodium_mg || 0), "mg"),
      protein_g: historyAmount(Number(meal.totals?.protein_g || 0), "g"),
      mixed_meal_ingredients: mixedMealIngredients,
      logged_at: new Date().toISOString(),
    };

    setSavingHistoryId(meal.id);
    try {
      if (guestMode) {
        const nextLogs = [
          {
            id: crypto.randomUUID(),
            ...historyPayload,
          },
          ...readJsonArray("guestFoodLogs"),
        ];
        localStorage.setItem("guestFoodLogs", JSON.stringify(nextLogs));
        toast.success(t("meal_mixer_history_saved"));
        setValidation("");
        return;
      }

      if (!user?.id) return;

      const { error } = await supabase.from("food_logs").insert({
        ...historyPayload,
        user_id: user.id,
      });
      if (error) throw error;
      toast.success(t("meal_mixer_history_saved"));
      setValidation("");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSavingHistoryId(null);
    }
  };

  return (
    <div className="space-y-5" dir={isArabic ? "rtl" : "ltr"} data-testid="meal-mixer-page">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight md:text-3xl">
            {t("nav_meal_mixer")}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            {t("meal_mixer_intro")}
          </p>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            {t("meal_mixer_helper")}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={resetMeal}
          className="gap-2 self-start"
          disabled={items.length === 0}
          data-testid="meal-mixer-reset"
        >
          <RotateCcw className="h-4 w-4" />
          {t("meal_mixer_reset")}
        </Button>
      </div>

      {aiMealDraft && (
        <section
          className="rounded-lg border border-primary/30 bg-primary/5 p-3"
          data-testid="meal-mixer-ai-draft"
        >
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div className="min-w-0">
                <h2 className="text-sm font-semibold">{t("meal_mixer_ai_draft_available")}</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  {aiMealDraft.mealName} {"\u00b7"} {(aiMealDraft.ingredients || []).length} {t("meal_mixer_ingredients")}
                </p>
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 md:flex md:shrink-0">
              <Button
                className="btn-primary h-9 px-3 text-sm"
                onClick={importAiMealDraft}
                data-testid="meal-mixer-ai-draft-import"
              >
                {t("meal_mixer_ai_draft_import")}
              </Button>
              <Button
                variant="outline"
                className="h-9 px-3 text-sm"
                onClick={removeAiMealDraft}
                data-testid="meal-mixer-ai-draft-dismiss"
              >
                {t("meal_mixer_ai_draft_dismiss")}
              </Button>
            </div>
          </div>
        </section>
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
        <section className="bento-card space-y-4">
          <div className="flex items-center gap-2">
            <Calculator className="h-5 w-5" style={{ color: "var(--rs-primary)" }} />
            <h2 className="text-lg font-semibold">
              {editingMealId ? t("meal_mixer_editing_saved") : t("meal_mixer_build")}
            </h2>
          </div>

          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_160px]">
            <div className="space-y-2">
              <Label htmlFor="meal-mixer-name">{t("meal_mixer_meal_name")}</Label>
              <Input
                id="meal-mixer-name"
                value={mealName}
                onChange={(event) => setMealName(event.target.value)}
                placeholder={t("meal_mixer_meal_name_placeholder")}
                className="h-11 rounded-lg bg-background"
                data-testid="meal-mixer-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="meal-mixer-quantity">{t("quantity")}</Label>
              <Input
                id="meal-mixer-quantity"
                type="number"
                min="0.1"
                step="0.1"
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
                className="h-11 rounded-lg bg-background"
                data-testid="meal-mixer-quantity"
              />
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_220px]">
            <div className="space-y-2">
              <Label htmlFor="meal-mixer-search">{t("search_food")}</Label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground rtl:left-auto rtl:right-3" />
                <Input
                  id="meal-mixer-search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={t("search_food")}
                  className="h-11 rounded-lg bg-background pl-9 rtl:pl-3 rtl:pr-9"
                  data-testid="meal-mixer-search"
                />
              </div>
              <p className="text-xs text-muted-foreground">{t("meal_mixer_more_foods")}</p>
            </div>

            <div className="space-y-2">
              <Label>{t("category")}</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-11 rounded-lg bg-background" data-testid="meal-mixer-category">
                  <SelectValue placeholder={t("category")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("all_categories")}</SelectItem>
                  {categories.map((item) => (
                    <SelectItem key={item} value={item}>
                      {displayCategory(item)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-border bg-background">
            <div className="border-b border-border bg-muted/50 px-3 py-2 text-xs font-semibold uppercase text-muted-foreground">
              {filteredFoods.length} {t("meal_mixer_foods_available")}
            </div>
            {filteredFoods.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                {t("no_food_matches")}
              </div>
            ) : (
              <div className="max-h-72 divide-y divide-border overflow-y-auto">
                {filteredFoods.map((food) => {
                  const active = String(food.id) === String(selectedFoodId);

                  return (
                    <button
                      key={food.id}
                      type="button"
                      onClick={() => setSelectedFoodId(String(food.id))}
                      className={`grid w-full gap-1 px-4 py-3 text-left transition-colors rtl:text-right ${
                        active ? "bg-primary/10 text-foreground" : "hover:bg-muted/50"
                      }`}
                      data-testid={`meal-mixer-food-option-${food.id}`}
                    >
                      <span className="font-semibold">{displayFoodName(food)}</span>
                      <span className="text-xs text-muted-foreground">
                        {displayCategory(food.category || t("not_listed"))} ·{" "}
                        {food.serving_size ? displayServing(food.serving_size) : t("serving_not_listed")}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <Button className="btn-primary h-11 w-full gap-2 md:w-auto" onClick={addItem} data-testid="meal-mixer-add">
            <Plus className="h-4 w-4" />
            {t("meal_mixer_add")}
          </Button>

          {validation && (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{validation}</span>
            </div>
          )}

          {selectedFood && (
            <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm">
              <div className="font-semibold">{displayFoodName(selectedFood)}</div>
              <div className="mt-1 text-xs text-muted-foreground">
                {displayCategory(selectedFood.category || t("not_listed"))} ·{" "}
                {selectedFood.serving_size ? displayServing(selectedFood.serving_size) : t("serving_not_listed")}
              </div>
              {selectedHasMissingData && (
                <p className="mt-2 text-xs text-muted-foreground">{t("meal_mixer_missing_selected")}</p>
              )}
            </div>
          )}

          <div className="overflow-hidden rounded-lg border border-border bg-card">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                {t("meal_mixer_empty")}
              </div>
            ) : (
              <div className="divide-y divide-border">
                {items.map((item) => {
                  const itemQuantity = parsePositiveQuantity(item.quantity);
                  const quantityInvalid = !itemQuantity;

                  return (
                    <div key={item.id} className="grid gap-2 p-3" data-testid="meal-mixer-item">
                      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                        <div className="min-w-0">
                          <div className="truncate font-semibold">{displayFoodName(item.food)}</div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {item.food?.serving_size ? displayServing(item.food.serving_size) : t("serving_not_listed")}
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-28">
                            <Label className="sr-only" htmlFor={`meal-mixer-item-${item.id}`}>
                              {t("quantity")}
                            </Label>
                            <Input
                              id={`meal-mixer-item-${item.id}`}
                              type="number"
                              min="0.1"
                              step="0.1"
                              value={item.quantity}
                              onChange={(event) => updateItemQuantity(item.id, event.target.value)}
                              className={`h-9 ${quantityInvalid ? "border-destructive" : ""}`}
                              data-testid="meal-mixer-item-quantity"
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => removeItem(item.id)}
                            aria-label={t("delete")}
                            data-testid="meal-mixer-remove"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {quantityInvalid && (
                        <div className="text-xs text-destructive">{t("meal_mixer_invalid_quantity")}</div>
                      )}

                      <div className="grid gap-1.5 sm:grid-cols-2 xl:grid-cols-4">
                        {NUTRIENTS.map((nutrient) => {
                          const contribution = itemQuantity
                            ? nutrientContribution(item.food, nutrient, itemQuantity)
                            : null;
                          return (
                            <div key={nutrient.key} className="rounded-md border border-border bg-background px-2.5 py-2">
                              <div className="text-[10px] font-semibold uppercase text-muted-foreground">
                                {t(nutrient.labelKey)}
                              </div>
                              <div className="text-sm font-bold">
                                {contribution === null
                                  ? t("meal_mixer_missing_data")
                                  : formatAmount(contribution, nutrient.unit)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <aside className="space-y-4">
          <section className="bento-card space-y-4">
            <h2 className="text-lg font-semibold">{t("meal_mixer_totals")}</h2>
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
              {NUTRIENTS.map((nutrient) => (
                <div key={nutrient.key} className="rounded-lg border border-border bg-background p-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-muted-foreground">{t(nutrient.labelKey)}</span>
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: nutrient.color }} />
                  </div>
                  <div className="mt-1 text-xl font-extrabold">
                    {formatAmount(totals[nutrient.key], nutrient.unit)}
                  </div>
                </div>
              ))}
            </div>
            <Button
              className="btn-primary h-11 w-full"
              onClick={() => saveMixedMeal()}
              disabled={!canSaveMix}
              data-testid="meal-mixer-save"
            >
              <Save className="mr-2 h-4 w-4 rtl:ml-2 rtl:mr-0" />
              {editingMealId ? t("meal_mixer_update_saved_meal") : t("meal_mixer_save_mixed_meal")}
            </Button>
            {editingMealId && (
              <Button
                variant="outline"
                className="h-11 w-full"
                onClick={() => saveMixedMeal({ saveAsNew: true })}
                disabled={!canSaveMix}
                data-testid="meal-mixer-save-new"
              >
                {t("meal_mixer_save_as_new")}
              </Button>
            )}
          </section>

          <section className="bento-card space-y-3">
            <h2 className="text-lg font-semibold">{t("meal_mixer_protein_target_title")}</h2>
            {proteinTarget === null ? (
              <p className="text-sm text-muted-foreground">{t("meal_mixer_update_weight")}</p>
            ) : (
              <div className="space-y-2 text-sm">
                <div>
                  <div className="text-muted-foreground">{t("meal_mixer_estimated_daily_target")}</div>
                  <div className="font-semibold">
                    {weightKg} kg x 1.2 = {formatAmount(proteinTarget, "g")}/{t("meal_mixer_day_unit")}
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background p-2.5">
                  <span className="text-muted-foreground">{t("meal_mixer_current_meal_protein")}</span>
                  <span className="font-semibold">{formatAmount(totals.protein_g, "g")}</span>
                </div>
                <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background p-2.5">
                  <span className="text-muted-foreground">{t("meal_mixer_remaining_today")}</span>
                  <span className="font-semibold">{formatAmount(remainingProtein, "g")}</span>
                </div>
              </div>
            )}
            <p className="text-xs leading-relaxed text-muted-foreground">
              {t("meal_mixer_protein_target_disclaimer")}
            </p>
          </section>

          <section className="rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
            {t("meal_mixer_disclaimer")}
          </section>
        </aside>
      </div>

      <section className="bento-card space-y-3" data-testid="saved-mixed-meals-section">
        <div>
          <h2 className="text-lg font-semibold">{t("meal_mixer_saved_section")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("meal_mixer_saved_helper")}</p>
        </div>

        {savedMeals.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
            {t("meal_mixer_saved_empty")}
          </div>
        ) : (
          <div className="grid gap-2 lg:grid-cols-2">
            {savedMeals.map((meal) => (
              <article
                key={meal.id}
                className="rounded-lg border border-border bg-background p-3"
                data-testid="saved-mixed-meal-card"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate text-base font-semibold">{meal.name}</h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {(meal.items || []).length} {t("meal_mixer_ingredients")}
                    </p>
                  </div>
                </div>

                <div className="mt-3 grid gap-1.5 sm:grid-cols-2 xl:grid-cols-4">
                  {NUTRIENTS.map((nutrient) => (
                    <div key={nutrient.key} className="rounded-md border border-border bg-card px-2.5 py-2">
                      <div className="text-[10px] font-semibold uppercase text-muted-foreground">
                        {t(nutrient.labelKey)}
                      </div>
                      <div className="text-sm font-bold">
                        {formatAmount(Number(meal.totals?.[nutrient.key] || 0), nutrient.unit)}
                      </div>
                    </div>
                  ))}
                </div>

                {mealHasMissingData(meal) && (
                  <p className="mt-3 text-xs text-muted-foreground">{t("meal_mixer_saved_missing_note")}</p>
                )}

                <div className="mt-3 grid gap-2 sm:grid-cols-3">
                  <Button
                    variant="outline"
                    className="h-9 gap-2 px-3 text-sm"
                    onClick={() => loadSavedMeal(meal)}
                    data-testid="saved-mixed-meal-load"
                  >
                    <Pencil className="h-4 w-4" />
                    {t("meal_mixer_load_edit")}
                  </Button>
                  <Button
                    className="btn-primary h-9 gap-2 px-3 text-sm"
                    onClick={() => addSavedMealToHistory(meal)}
                    disabled={savingHistoryId === meal.id}
                    data-testid="saved-mixed-meal-add-history"
                  >
                    <CalendarPlus className="h-4 w-4" />
                    {savingHistoryId === meal.id ? t("loading") : t("meal_mixer_add_saved_history")}
                  </Button>
                  <Button
                    variant="outline"
                    className="h-9 gap-2 px-3 text-sm text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => deleteSavedMeal(meal.id)}
                    data-testid="saved-mixed-meal-delete"
                  >
                    <Trash2 className="h-4 w-4" />
                    {t("delete")}
                  </Button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
